import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Clock, Phone, Navigation, ArrowUpRight } from "lucide-react";

const isOpenNow = (opensAt = 10) => {
  const rome = new Date(new Date().toLocaleString("en-US", { timeZone: "Europe/Rome" }));
  if (rome.getDay() === 1) return false;
  const mins = rome.getHours() * 60 + rome.getMinutes();
  return mins >= opensAt * 60 && mins < 1320;
};

const mapsEmbed = (q) =>
  `https://maps.google.com/maps?q=${encodeURIComponent(q)}&z=16&output=embed`;
const mapsDir = (q) =>
  `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(q)}`;

export default function StoreCard({ store, t }) {
  const open = isOpenNow(store.opens_at);
  const images = [store.image, ...(store.gallery || [])];
  const [active, setActive] = useState(0);

  return (
    <article
      data-testid={`store-card-${store.id}`}
      className="group bg-white rounded-3xl overflow-hidden border border-[#241C1A]/10 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-[box-shadow,transform] duration-300 flex flex-col"
    >
      <div className="relative overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.img
            key={images[active]}
            src={images[active]}
            alt={store.alt}
            data-testid={`store-image-${store.id}`}
            initial={{ opacity: 0.4 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.35 }}
            loading="lazy"
            className="w-full aspect-[4/3] object-cover group-hover:scale-[1.04] transition-transform duration-700"
          />
        </AnimatePresence>
        <span className="absolute top-4 left-4 font-accent text-[10px] uppercase tracking-[0.18em] bg-[#FAF7F2]/95 text-[#241C1A] px-3 py-1.5 rounded-full">
          {store.badge}
        </span>
        <span
          data-testid={`open-badge-${store.id}`}
          className={`absolute top-4 right-4 inline-flex items-center gap-1.5 font-accent text-[10px] uppercase tracking-[0.14em] px-3 py-1.5 rounded-full ${
            open ? "bg-[#506334] text-[#FAF7F2]" : "bg-[#1E1614]/85 text-[#FAF7F2]"
          }`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${open ? "bg-[#B7D97B]" : "bg-[#C86D51]"}`} />
          {open ? t.stores.open : t.stores.closed}
        </span>
      </div>

      {images.length > 1 && (
        <div className="flex gap-2 px-6 sm:px-7 pt-4">
          {images.map((img, i) => (
            <button
              key={img}
              data-testid={`gallery-thumb-${store.id}-${i}`}
              onClick={() => setActive(i)}
              aria-label={`Foto ${i + 1} di ${store.city}`}
              className={`w-14 h-14 rounded-xl overflow-hidden border-2 transition-all duration-300 ${
                active === i ? "border-[#C86D51] scale-105" : "border-transparent opacity-60 hover:opacity-100"
              }`}
            >
              <img src={img} alt="" loading="lazy" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      <div className="p-6 sm:p-7 pt-4 flex flex-col flex-1">
        <h3 className="font-display text-3xl text-[#241C1A] tracking-tight">{store.city}</h3>

        <ul className="mt-4 space-y-3 text-sm text-[#6E625E]">
          <li className="flex items-start gap-2.5">
            <MapPin size={16} className="mt-0.5 shrink-0 text-[#C86D51]" />
            <span>
              {store.address}
              <br />
              {store.cap}
            </span>
          </li>
          <li className="flex items-start gap-2.5">
            <Clock size={16} className="mt-0.5 shrink-0 text-[#C86D51]" />
            <span>
              {store.hours}
              <br />
              <span className="text-[#A84C32]">{t.stores.closedDay}</span>
            </span>
          </li>
          <li className="flex items-center gap-2.5">
            <Phone size={16} className="shrink-0 text-[#C86D51]" />
            <a
              href={`tel:${store.phone_tel}`}
              data-testid={`call-button-${store.id}`}
              className="font-accent hover:text-[#C86D51] transition-colors"
            >
              {store.phone_display}
            </a>
          </li>
        </ul>

        <div className="mt-6 flex flex-wrap gap-3">
          <a
            href={mapsDir(store.maps_query)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`directions-button-${store.id}`}
            className="inline-flex items-center gap-2 rounded-full border border-[#241C1A]/20 px-5 py-2.5 text-sm font-medium text-[#241C1A] hover:border-[#C86D51] hover:text-[#C86D51] transition-colors duration-300"
          >
            <Navigation size={15} /> {t.stores.directions}
          </a>
          {store.order ? (
            <a
              href={store.order.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`order-button-${store.id}`}
              className="inline-flex items-center gap-2 rounded-full bg-[#506334] text-[#FAF7F2] px-5 py-2.5 text-sm font-medium hover:bg-[#3D4D27] transition-colors duration-300"
            >
              {t.stores.orderOn} {store.order.platform} <ArrowUpRight size={15} />
            </a>
          ) : (
            <span
              data-testid={`no-delivery-badge-${store.id}`}
              className="inline-flex items-center rounded-full bg-[#F3ECE0] text-[#6E625E] px-5 py-2.5 text-sm font-medium"
            >
              {t.stores.noDelivery}
            </span>
          )}
        </div>

        <div className="mt-6 rounded-2xl overflow-hidden border border-[#241C1A]/10">
          <iframe
            title={`Mappa Fiordipanna ${store.city}`}
            data-testid={`map-iframe-${store.id}`}
            src={mapsEmbed(store.maps_query)}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="w-full h-48 grayscale-[35%] contrast-[1.02]"
            allowFullScreen
          />
        </div>
      </div>
    </article>
  );
}
